import React, { useEffect, useMemo, useState } from 'react'
import { SubscriptionPaymentRecurrence } from '../models/SubscriptionPaymentRecurrence'
import { Paper, Alert } from '@mui/material';
import { differenceInDays, format, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getNextDueDate, RestaurantSubscription } from '../models/RestaurantSubscription';
import { errorColor, primary } from '../styles/colors';
import useFetch from '../hooks/useFetch';
import { getValidPriceOnModify, postModifyPaymentMethod } from '../config/apiRoutes';
import { paymentSettingsRoute } from '../config/pageRoutes';
import ConfirmationDialog from '../modal/ConfirmationDialog';
import SmallButton from '../buttons/SmallButton';

type Props = {
    paymentRecurrence: SubscriptionPaymentRecurrence;
    currentSubscription: RestaurantSubscription;
}

const CardInfo = ({ paymentRecurrence, currentSubscription }: Props) => {
    const loader = useFetch();
    const { t, i18n } = useTranslation();
    const [isConfirmationModalOpen, setIsconfirmationModalOpen] = useState(false);
    const nextDueDate = useMemo(() => {
        if (currentSubscription.nextDueDate == null) return "";
        const nextDueDate = getNextDueDate(currentSubscription.paymentInterval, new Date(currentSubscription.nextDueDate));
        return format(nextDueDate, "yyyy-MM-dd");
    }, [currentSubscription.nextDueDate]);
    const [nextPrice, setNextPrice] = useState<number>(0);
    const showCardExpirationWarning = useMemo(() => {
        if (paymentRecurrence.cardExpirationDate == null) {
            return false;
        }
        const daysUntilExpiration = differenceInDays(paymentRecurrence.cardExpirationDate, new Date());
        return daysUntilExpiration <= 60;
    }, []);

    useEffect(() => {
        fetchPriceOnModify();
    }, []);

    async function fetchPriceOnModify() {
        const url = getValidPriceOnModify
            .replace(":restaurantShortName", paymentRecurrence.restaurantShortName)
            .replace(":recurrenceId", paymentRecurrence.id.toString());
        const response = await loader.call<null, { priceGross: number }>("GET", url);
        if (response.ok && response.data) {
            setNextPrice(response.data.priceGross);
        }
    }

    function handleConfirmClick() {
        handleModifyPaymentMethod();
    }

    async function handleModifyPaymentMethod() {
        const url = postModifyPaymentMethod.replace(":restaurantShortName", paymentRecurrence.restaurantShortName);
        const body = {
            recurrenceId: paymentRecurrence.id,
            locale: i18n.language,
            redirectUrl: `${window.location.origin}${paymentSettingsRoute.path.replace(":lang", i18n.language)}`
        }

        const response = await loader.call<any, any>("POST", url, body);
        if (response.ok && response.data) {
            const url = response.data.redirectUrl;
            const newTab = window.open(url, '_blank');
            if (newTab) {
                newTab.focus();
            }
        }
    }

    return (
        <>
            <Paper>
                <div className="px-2 py-2 flex flex-row gap-2">
                    <div className="grow">
                        <div className="font-bold">
                            {paymentRecurrence.cardType}
                        </div>
                        <div className="font-bold" >
                            **** **** **** {paymentRecurrence.cardMaskedPan}
                        </div>
                        {paymentRecurrence.cardExpirationDate && (
                            <div color={showCardExpirationWarning ? errorColor : "black"} className={showCardExpirationWarning ? "font- bold" : "font-normal"}>
                                {format(subMonths(paymentRecurrence.cardExpirationDate, 1), "MM/yyyy")}
                            </div>
                        )}
                    </div>
                    <div className="mt-4">
                        <SmallButton
                            variant="primary"
                            onClick={() => setIsconfirmationModalOpen(true)}
                        >
                            {t("payment.update_card")}
                        </SmallButton>
                    </div>
                </div>
                {showCardExpirationWarning && (
                    <Alert severity='error' variant="filled">
                        {t("payment.bank_card_expires_soon")}
                    </Alert>
                )}
            </Paper>
            <ConfirmationDialog
                open={isConfirmationModalOpen}
                onClose={() => setIsconfirmationModalOpen(false)}
                onConfirm={handleConfirmClick}
                onCancel={() => setIsconfirmationModalOpen(false)}
                texts={{
                    title: t("payment.change_payment_method"),
                    confirm: t("common.continue"),
                    cancel: t("common.cancel")
                }}
            >
                <div className="text-[15px] text-primary-dark mb-4">
                    {t("payment.change_payment_method_tooltip")}
                </div>
                <Alert severity='success' icon={false}>
                    <div className="text-left">
                        {t("payment.amount_to_be_paid")}: <strong style={{ color: primary.dark }}>{nextPrice} Ft</strong>
                    </div>
                    <div className="text-left" >
                        {t("payment.new_subscription_valid_until")}: <strong style={{ color: primary.dark }}>{nextDueDate}</strong>
                    </div>
                </Alert>
                <div className="mt-2 flex justify-end">
                    <img className="h-[50px]" src="/assets/barion-banner-lightmode.svg" alt="barion-banner" />
                </div>
            </ConfirmationDialog>
        </>
    )
}

export default CardInfo